import React, { useMemo, useState } from "react";
import { ArrowLeftRight, Banknote, Loader2, PackageCheck, Plus, RefreshCw, Search } from "lucide-react";
import MovementModal from "../components/MovementModal";
import StockActionModal, { type StockAction } from "../components/StockActionModal";
import StoreShell from "../components/StoreShell";
import { usePermission } from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContexts";
import {
  MovementType,
  useStoreGetItemsQuery,
  useStoreGetMovementsQuery,
  useStoreGetStoresQuery,
} from "../api/storesApi";
import {
  fmtDate, fmtQty, monthRange, movementColor, movementLabel, recipient, signedQty,
} from "../utils/stores";

const labelCls = "block text-xs font-medium text-gray-600 mb-1";
const fieldCls = "px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100";

const TYPE_TABS = [
  { key: "", label: "All" },
  { key: "in", label: "Receipts" },
  { key: "out", label: "Issues" },
  { key: "adjustment", label: "Adjustments" },
] as const;

/** The movement register — every receipt, issue and correction, filterable. */
export default function StoreMovementsPage() {
  const thisMonth = monthRange();
  const [type, setType] = useState<MovementType | "">("");
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(thisMonth.to);
  const [itemId, setItemId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [q, setQ] = useState("");
  const [recording, setRecording] = useState(false);
  /** Which two-ended move is being recorded, if any. */
  const [action, setAction] = useState<StockAction | null>(null);

  // Reading the register is store.access (the route guard); recording into it
  // needs store.movement. super_admin is bypassed as RequirePermission does.
  const { can } = usePermission();
  const { hasRole } = useAuth();
  const canMove = hasRole("super_admin") || can("store.movement");

  const { data, isLoading, isFetching, refetch } = useStoreGetMovementsQuery({
    type,
    from,
    to,
    store_item_id: itemId || undefined,
    store_id: storeId || undefined,
  });
  const { data: itemsData } = useStoreGetItemsQuery({});
  const { data: stores = [] } = useStoreGetStoresQuery();

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return rows;
    return rows.filter((r: any) =>
      [r.item?.name, r.item?.code, r.reference, r.supplier, r.issued_to, r.note, recipient(r)]
        .filter(Boolean).join(" ").toLowerCase().includes(nq)
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    let received = 0;
    let issued = 0;
    for (const r of filtered) {
      if (r.type === "in") received += Number(r.quantity ?? 0);
      if (r.type === "out") issued += Number(r.quantity ?? 0);
    }
    return { received, issued };
  }, [filtered]);

  return (
    <StoreShell>
    <div>
      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Stock Movements</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Everything received into and issued out of the store, with the balance each movement left behind.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canMove && (
              <>
                <button
                  onClick={() => setRecording(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" /> Receive / issue
                </button>
                {/* The moves with two ends — another store, or a person. */}
                <button
                  onClick={() => setAction("transfer")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeftRight className="w-4 h-4" /> Transfer
                </button>
                <button
                  onClick={() => setAction("issue")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <PackageCheck className="w-4 h-4" /> To staff
                </button>
                <button
                  onClick={() => setAction("sale")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Banknote className="w-4 h-4" /> Sale
                </button>
              </>
            )}
            <button onClick={() => refetch()} disabled={isFetching} className="p-2 rounded-lg border hover:bg-gray-50" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Type tabs */}
        <div className="mt-5 flex flex-wrap gap-2">
          {TYPE_TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setType(t.key as MovementType | "")}
              className={`px-4 py-2 text-sm border rounded-lg transition-colors ${
                type === t.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div>
            <label className={labelCls}>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={fieldCls} />
          </div>
          <div className="min-w-[12rem]">
            <label className={labelCls}>Item</label>
            <select value={itemId} onChange={(e) => setItemId(e.target.value)} className={`${fieldCls} w-full`}>
              <option value="">All items</option>
              {(itemsData?.rows ?? []).map((i: any) => (
                <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
              ))}
            </select>
          </div>
          {/* One store's own card. The other side of a transfer belongs to the
              receiving store's card, so it is not shown here. */}
          <div className="min-w-[11rem]">
            <label className={labelCls}>Store</label>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className={`${fieldCls} w-full`}>
              <option value="">All stores</option>
              {stores.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Reference, supplier, recipient…"
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            {rows.length === 0 ? "No movements in this period." : "No movements match your filters."}
          </div>
        ) : (
          <>
            <p className="mt-5 text-xs text-gray-500">
              {filtered.length} movement{filtered.length === 1 ? "" : "s"} · {fmtQty(totals.received)} received ·{" "}
              {fmtQty(totals.issued)} issued
            </p>

            <div className="mt-2 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                    <th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-4 py-2 font-medium">Store</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium text-right">Qty</th>
                    <th className="px-4 py-2 font-medium text-right">Balance</th>
                    <th className="px-4 py-2 font-medium">From / To</th>
                    <th className="px-4 py-2 font-medium">Recorded by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((r: any) => (
                    <tr key={r.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(r.movement_date)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.item?.name ?? "—"}</div>
                        <div className="text-xs text-gray-400 font-mono">{r.item?.code}</div>
                      </td>
                      {/* Whose balance this line moved: a store's, or a member
                          of staff's once the stock has left the stores. */}
                      <td className="px-4 py-3 text-gray-700">
                        {r.holder_type === "admin"
                          ? <span className="text-xs">Held by {recipient(r)}</span>
                          : <>
                              <div>{r.store?.name ?? "—"}</div>
                              {r.counterpart_store?.name && (
                                <div className="text-xs text-gray-400">
                                  {r.type === "transfer_out" ? "→ " : "← "}{r.counterpart_store.name}
                                </div>
                              )}
                            </>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${movementColor(r.type)}`}>
                          {movementLabel(r.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-800">
                        {signedQty(r)}
                        <span className="text-gray-400 text-xs"> {r.item?.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmtQty(r.balance_after)}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.type === "out" ? recipient(r) : r.supplier || "—"}
                        {r.department?.name && <div className="text-xs text-gray-400">{r.department.name}</div>}
                        {r.reference && <div className="text-xs text-gray-400">Ref: {r.reference}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {r.recorded_by?.name ?? r.recordedBy?.name ?? "—"}
                        {r.note && (
                          <div className="text-gray-400 truncate max-w-[160px]" title={r.note}>{r.note}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {recording && <MovementModal type="out" onClose={() => setRecording(false)} />}
      {action && <StockActionModal action={action} onClose={() => setAction(null)} />}
    </div>
    </StoreShell>
  );
}
